import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, LayoutDashboard, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, fmtDate, fmtNum } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { DataHealthBanner } from "@/components/DataHealth";
import { PlantCompare } from "@/components/PlantCompare";

const BASIS = { in: "in scope & in stock", all: "all materials, zero stock included" };

const ActionButton = ({ a, i }) => (
  <Link to={a.to} data-testid={`dash-action-${a.key}`}
    className={`rise group flex items-center gap-4 rounded-xl border bg-white p-4 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md ${a.done ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 hover:border-[#D52B3B]/40"}`}
    style={{ animationDelay: `${60 + i * 40}ms` }}>
    <span className="w-6 shrink-0 font-mono text-xs text-slate-400">{i + 1}</span>
    <div className="min-w-0 flex-1">
      <p className="font-medium text-slate-900" data-testid={`dash-action-${a.key}-label`}>{a.label}</p>
      <p className="mt-0.5 text-sm text-slate-500" data-testid={`dash-action-${a.key}-todo`}>{a.todo}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400" data-testid={`dash-action-${a.key}-owner`}>Owner: {a.owner}</p>
      {a.sub && <p className="mt-1 text-xs text-slate-600" data-testid={`dash-action-${a.key}-sub`}>{a.sub}</p>}
    </div>
    {a.state === "not_started" && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid={`dash-action-${a.key}-state`}>{a.status_label}</span>}
    {a.no_data
      ? <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700" data-testid={`dash-action-${a.key}-nodata`}>No data</span>
      : a.done
      ? <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700" data-testid={`dash-action-${a.key}-done`}><CheckCircle2 className="h-4 w-4" />Done</span>
      : <span className="font-mono text-2xl font-semibold tabular-nums text-[#D52B3B]" data-testid={`dash-action-${a.key}-count`}>{fmtNum(a.count)}</span>}
    <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-600" />
  </Link>
);

export default function Dashboard() {
  const { params, label } = usePlant();
  const { user } = useAuth();
  const am = user?.role === "account_manager";
  const [d, setD] = useState(null);
  const [stock, setStock] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("actions");
  const load = useCallback((refresh) => {
    setBusy(true);
    const p = { ...(am ? {} : params), ...(stock ? { stock } : {}), ...(refresh ? { refresh: true } : {}) };
    api.get("/dashboard/actions", { params: p }).then((r) => setD(r.data)).finally(() => setBusy(false));
  }, [params, stock, am]);
  useEffect(() => { setD(null); load(false); }, [load]);
  const basis = stock || d?.stock || "in";
  return (
    <div data-testid="action-dashboard" className="max-w-5xl">
      <PageHeader title="Dashboard" subtitle={`${basis === "in" ? "In-stock in-scope items" : "All materials, zero stock included"}. Each button opens the exact list to work through; a count of 0 is Done.`}>
        <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700" data-testid="dash-stock-toggle-label">
          <Switch checked={basis === "in"} onCheckedChange={(v) => setStock(v ? "in" : "all")} disabled={!d && busy} data-testid="dash-stock-toggle" />
          In scope &amp; in stock only
        </label>
        <Button asChild variant="ghost" data-testid="dash-detailed-link"><Link to="/dashboard/detailed"><LayoutDashboard className="mr-2 h-4 w-4" />Detailed dashboard</Link></Button>
        <Button variant="outline" data-testid="dash-refresh" disabled={busy} onClick={() => load(true)}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Refresh</Button>
      </PageHeader>
      {!am && (
        <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="dash-view-tabs">
          {[["actions", "Actions"], ["compare", "Compare plants"]].map(([k, t]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`dash-tab-${k}`}
              className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors duration-150 ${view === k ? "bg-[#1C1C1E] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{t}</button>
          ))}
        </div>
      )}
      {view === "compare" && !am ? <PlantCompare stock={basis} /> : <>
      {basis === "in" && <DataHealthBanner health={d?.health} />}
      <p className="mb-4 text-xs text-slate-500" data-testid="dash-as-at">
        {d ? `As at ${fmtDate(d.as_at, true)} · Basis: ${BASIS[d.stock]}${am ? "" : ` · ${label}`}` : "Loading…"}
      </p>
      <div className="space-y-2.5">
        {d ? d.items.map((a, i) => <ActionButton key={a.key} a={a} i={i} />) : Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}
      </div>
      </>}
    </div>
  );
}
